import * as React from "react";

import Layout from "../../components/Layout";
import styled from "styled-components";

import {
  getListOfUsers,
  User,
  createUser,
  updateUser,
  resetPassword
} from "../../api/users";
import { getAvailableLanguages } from "../../api/lang";

import { Spinner, Button, TextInput } from "@hubper/core-ui/elements";
import { Header } from "@hubper/core-ui/typo";

import { useHistory, useParams } from "react-router-dom";
import { ToggleList } from "@hubper/core-ui/components";

import { useNotify } from "@hubper/core-ui/hooks";

/**
 * Styles
 */

const Main = styled.div`
  width: 100%;
`;

const Body = styled.div`
  display: flex;
  width: 100%;
  margin-top: 64px;
  margin-bottom: 48px;
`;

const Top = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 32px;
`;

const Left = styled.div`
  width: 50%;
  margin-right: 48px;
`;
const Right = styled.div`
  width: 50%;
`;

/**
 * Main
 */

const emailPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const ALL_PERMISSIONS = [
  "SETTINGS",
  "MANAGE_USERS",
  "BACKUP",
  "LANGUAGE_CREATE",
  "LANGUAGE_DELETE",
  "NS_CREATE",
  "NS_DELETE",
  "NS_MODIFY",
  "ENTRY_CREATE",
  "ENTRY_DELETE",
  "ENTRY_MODIFY",
  "KEY_MODIFY"
];

type Errors = {
  name: string | null;
  email: string | null;
};

function EditUser({ create }: { create?: boolean }) {
  const [availableLanguages, setAvailableLanguages] = React.useState<string[]>(
    []
  );
  const [user, setUser] = React.useState<User | null>(
    create
      ? {
          email: "",
          id: "tmp",
          languages: ["*"],
          permissions: ["ENTRY_MODIFY"],
          name: ""
        }
      : null
  );

  const [password, setPassword] = React.useState<string | null>(null);

  const [errors, setErrors] = React.useState<Errors>({
    email: null,
    name: null
  });

  const history = useHistory();
  const { id: userId } = useParams<{ id: string }>();

  const notify = useNotify();

  React.useEffect(() => {
    getAvailableLanguages().then(setAvailableLanguages);
    if (!create) {
      getListOfUsers().then(x => setUser(x.find(u => u.id === userId)!));
    }
  }, []);

  return (
    <Layout
      bodyStyle={{
        position: "relative",
        display: "flex"
      }}
    >
      <Main>
        <h1 style={{ margin: 0 }}>Users - {create ? "New" : "Edit"}</h1>

        {!user ? (
          <Spinner />
        ) : (
          <>
            <Top>
              {user.id !== "tmp" && (
                <Button
                  text="Reset password"
                  type="grey"
                  style={{ marginRight: 12 }}
                  onClick={async () => {
                    try {
                      const { password } = await resetPassword(user.id);
                      setPassword(password);
                      notify({
                        title: "Created new password",
                        description: user.name,
                        type: "success"
                      });
                    } catch (e) {
                      notify({
                        title: "Error creating new password",
                        description: user.name,
                        type: "error"
                      });
                    }
                  }}
                />
              )}
              <Button
                text={create ? "Create user" : "Save user"}
                type="color"
                onClick={async () => {
                  // validate shit
                  const errors: Errors = {
                    name: null,
                    email: null
                  };

                  if (user.name.length === 0) {
                    errors.name = "This field is required.";
                  }
                  if (user.email.length === 0) {
                    errors.email = "This field is required.";
                  } else if (!emailPattern.test(user.email)) {
                    errors.email = "Please provide a valid e-mail.";
                  }

                  if (errors.email || errors.name) {
                    setErrors(errors);
                    return;
                  }

                  if (create) {
                    try {
                      const { password, id } = await createUser(user);
                      setPassword(password);

                      notify({
                        title: "User created",
                        description: user.name,
                        type: "success"
                      });

                      history.replace(`/users/${id}`);
                    } catch (e) {
                      notify({
                        title: "Error creating user",
                        description: user.name,
                        type: "error"
                      });
                    }
                    return;
                  }

                  try {
                    await updateUser(user.id, user);

                    notify({
                      title: "User updated",
                      description: user.name,
                      type: "success"
                    });
                  } catch (e) {
                    notify({
                      title: "Error updating user",
                      description: user.name,
                      type: "error"
                    });
                  }
                }}
              />
            </Top>
            <Body>
              <Left>
                <Header style={{ marginBottom: 24 }}>Basic</Header>
                <TextInput
                  value={user.name}
                  onChange={(evt: any) =>
                    setUser({ ...user, name: evt.target.value })
                  }
                  label="Name"
                  error={errors.name || undefined}
                />
                <TextInput
                  value={user.email}
                  label="E-mail"
                  onChange={(evt: any) =>
                    setUser({ ...user, email: evt.target.value })
                  }
                  style={{ marginTop: 32 }}
                  error={errors.email || undefined}
                />
                {password && (
                  <TextInput
                    value={password}
                    label="Password"
                    onChange={() => null}
                    style={{ marginTop: 32 }}
                    disabled
                  />
                )}

                <Header style={{ marginBottom: 24, marginTop: 32 }}>
                  Languages
                </Header>
                <ToggleList
                  stretch
                  items={availableLanguages.map(x => ({ id: x, name: x }))}
                  onChange={(value: any) =>
                    setUser({ ...user, languages: value })
                  }
                  selectedItems={user.languages}
                />
              </Left>
              <Right>
                <Header style={{ marginBottom: 24 }}>Permissions</Header>
                <ToggleList
                  stretch
                  items={ALL_PERMISSIONS.map(x => ({ id: x, name: x }))}
                  onChange={(value: any) =>
                    setUser({ ...user, permissions: value })
                  }
                  selectedItems={user.permissions}
                  search
                  searchPlaceholder={"Filter permissions"}
                />
              </Right>
            </Body>
          </>
        )}
      </Main>
    </Layout>
  );
}

export default EditUser;
